import { z } from 'zod'
import { oc } from '@orpc/contract'
// Type-only (erased at build) — used solely by the drift guards below. The Zod schemas are defined
// here, in the contract, because the web bundles *.contract.ts across the package boundary and may
// not pull in other (non-contract) middleware modules.
import type { Artifacts } from '../../model/artifacts/types'
import type { DetailsMeta } from '../../model/clinical-summary/details/types'
import { artifactsSchema } from '../../model/artifacts/artifacts.schema'

/**
 * The clinical-summary details endpoint returns renderable ARTIFACTS (charts + tables, each with
 * an id and a `group` section path — see model/clinical-summary/details/to-artifacts.ts), NOT the
 * structured dataset: the chart tab renders them with the same components the assistant uses, so
 * both surfaces present the summary identically. `meta` rides alongside for the completeness
 * (incomplete-data) notice; section order is the artifacts' emission order.
 */

// Exported so the overview contract can reuse it (the overview carries the details meta it was
// built from). Keep in sync with DetailsMeta in model/clinical-summary/details/types.ts.
export const detailsMetaSchema = z.object({
  // ClinicalSummaryDetails types this as Partial<Record<string, number>> — values may be absent.
  resourceCounts: z.record(z.string(), z.number().optional()),
  truncatedResources: z.string().array(),
  excludedLabRowCount: z.number(),
  unparsedDiagnosticReports: z.number(),
  incomplete: z.boolean().optional(),
})

export const clinicalSummaryDetailsArtifactsSchema = z.object({
  patientId: z.string(),
  generatedAt: z.string(),
  meta: detailsMetaSchema,
  artifacts: artifactsSchema,
})

// Compile-time drift guards: the meta the model produces and the artifacts the converter produces
// must remain assignable to the contract output types. If either shape changes and this schema is
// not updated to match, these assignments fail to type-check.
type ContractOutput = z.infer<typeof clinicalSummaryDetailsArtifactsSchema>
type AssertMetaAssignable = DetailsMeta extends ContractOutput['meta'] ? true : never
const _assertMetaAssignable: AssertMetaAssignable = true
void _assertMetaAssignable
type AssertArtifactsAssignable = Artifacts extends ContractOutput['artifacts'] ? true : never
const _assertArtifactsAssignable: AssertArtifactsAssignable = true
void _assertArtifactsAssignable

// Deterministic clinical-summary-details artifacts for a single patient (see
// model/clinical-summary/details). Uses getClinicalSummaryDetails's defaults (unresolved
// diagnoses, recent de-duplicated medications, abnormal labs); the `with` scope is intentionally
// not exposed as query params yet.
export const patientClinicalSummaryDetails = oc
  .route({ method: 'GET', path: '/v2/patient/{id}/clinical-summary-details' })
  .input(z.object({ id: z.string() }))
  .output(clinicalSummaryDetailsArtifactsSchema)
